import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useDebouncedValue } from '@mantine/hooks'

import { useInsightsContext } from '../context/InsightsContext'
import { FilterValueOption } from '../types/insights.types'
import { cursorFromLink } from '../utils/cursorFromLink'

const DEFAULT_PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 250
const VALUES_STALE_TIME_MS = 30_000

export type UseFilterValuesOptions = {
  /** Fetch only while `true` — enable on dropdown open, so a bar of filters
   * doesn't each fire a request on page load. */
  enabled?: boolean
  pageSize?: number
  /**
   * A dependent picker's scope: the current selection of each filter this one
   * `dependsOn`, sent as ordinary filter params so the endpoint narrows the values
   * to it. Part of the query key, so changing a dependency refetches from page one.
   * Empty keys are dropped — an unselected dependency narrows nothing.
   */
  scope?: Record<string, string[] | undefined>
}

export type UseFilterValuesResult = {
  /** Options accumulated across every loaded page for the active search. */
  options: FilterValueOption[]
  /** Live (un-debounced) search term — bind straight to the input. */
  search: string
  setSearch: (value: string) => void
  /** The query has resolved at least once (so `options` can be trusted over any
   * inline seed, even when empty). */
  hasLoaded: boolean
  /** First page loading (no data yet) for the active search. */
  isLoading: boolean
  /** A "load more" page is loading. */
  isLoadingMore: boolean
  hasMore: boolean
  loadMore: () => void
  error: boolean
}

/**
 * Searchable, keyset-paginated values for one `values`-sourced filter, via the
 * domain's `getFilterValues` endpoint (NEX-746). Search is debounced and resets
 * pagination; `loadMore` appends the next page. Gated on `enabled` and on the
 * host wiring `getFilterValues`.
 */
export const useFilterValues = (
  filter: string,
  {
    enabled = true,
    pageSize = DEFAULT_PAGE_SIZE,
    scope,
  }: UseFilterValuesOptions = {}
): UseFilterValuesResult => {
  const { domain, service } = useInsightsContext()
  const getFilterValues = service.getFilterValues

  // Only dependencies with a live selection travel; sorted by key so the same scope
  // built in a different order keys the cache the same.
  const scopeParams = Object.fromEntries(
    Object.entries(scope ?? {})
      .filter(
        (entry): entry is [string, string[]] => (entry[1]?.length ?? 0) > 0
      )
      .sort(([a], [b]) => a.localeCompare(b))
  )

  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS)
  const trimmedSearch = debouncedSearch.trim()

  const query = useInfiniteQuery({
    // Search and the dependency scope are part of the key, so changing either starts
    // a fresh first-page query.
    queryKey: [
      `insights/${domain}/filters/${filter}/values`,
      { search: trimmedSearch, scope: scopeParams },
    ],
    queryFn: ({ pageParam }) => {
      // `enabled` already gates this on getFilterValues being wired; the guard is
      // kept because TypeScript can't narrow the optional method from `enabled`.
      /* istanbul ignore next -- unreachable: `enabled` gates queryFn on getFilterValues */
      if (!getFilterValues) {
        return Promise.reject(new Error('getFilterValues is not wired'))
      }

      return getFilterValues({
        domain,
        filter,
        params: {
          pageSize,
          ...scopeParams,
          ...(trimmedSearch ? { search: trimmedSearch } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => cursorFromLink(lastPage.data._links?.next),
    enabled: enabled && Boolean(getFilterValues),
    // Keep the prior search's results visible while the next term's first page
    // loads, so a keystroke doesn't flash a spinner / "no results" mid-fetch.
    placeholderData: keepPreviousData,
    staleTime: VALUES_STALE_TIME_MS,
    // Retry a couple of times so a transient blip doesn't disable the control
    // (which then wouldn't refetch until staleTime elapses on reopen).
    retry: 2,
  })

  const options = query.data?.pages.flatMap((page) => page.data.data) ?? []

  // Stable reference (fetchNextPage is itself stable) so consumers can safely put
  // loadMore in effect deps — e.g. the IntersectionObserver setup — without it
  // tearing down and rebuilding every render.
  const { fetchNextPage } = query
  const loadMore = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])

  return {
    options,
    search,
    setSearch,
    // The query has resolved at least once (data stays defined across search
    // changes via keepPreviousData) — lets the caller stop seeding with the
    // inline page and trust `options`, even when a page comes back empty.
    hasLoaded: query.data !== undefined,
    isLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
    hasMore: Boolean(query.hasNextPage),
    loadMore,
    error: query.isError,
  }
}
